// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The ball.
//
// Planar position and velocity on the playfield, an explicit height layer for ramps and
// airballs, and a full 3-D orientation carried for rendering the spin and for coupling
// friction into angular momentum. See 02 §4.

import { BALL_INV_INERTIA, BALL_INV_MASS, BALL_RADIUS, SPEED_CLAMP } from './constants'

export class Ball {
  readonly id: number

  /** In play. A ball in the trough is inactive and is not simulated. */
  active = false

  // --- planar state, playfield-local metres ---
  x = 0
  y = 0
  vx = 0
  vy = 0

  // --- height state ---
  /** Height above the playfield surface. 0 while rolling on it. */
  z = 0
  vz = 0
  /** Index into the world's layer table. */
  layer = 0

  // --- angular state, radians per second about playfield-local axes ---
  wx = 0
  wy = 0
  wz = 0

  // --- orientation, unit quaternion, for rendering only ---
  qx = 0
  qy = 0
  qz = 0
  qw = 1

  readonly radius = BALL_RADIUS
  readonly invMass = BALL_INV_MASS
  readonly invInertia = BALL_INV_INERTIA

  /** Steps since this ball last exceeded REST_SPEED. Drives stuck-ball detection (PH-090). */
  stillSteps = 0

  /**
   * Index of the device that owns this ball's motion, or −1 when the ball is free.
   *
   * A ball in a scoop is not simulated at all: it is not falling, not rolling, and not
   * resting against anything. A ball in a wire habitrail has exactly one degree of freedom
   * and the wire moves it along its own arc length. Both are the same idea — a mechanism
   * that has taken the ball out of the general solver's hands — and modelling them that way
   * rather than as very strong forces is both cheaper and impossible to jitter.
   */
  heldBy = -1

  /**
   * Position at the start of the current step, for render interpolation only.
   *
   * The simulation runs at a fixed 1000 Hz and the display does not. A renderer that drew
   * the latest state each frame would sample an unsynchronised clock and judder; one that
   * advanced the simulation to meet the frame would make the game depend on the display.
   * Keeping the previous step's position lets the renderer draw *between* two real states
   * and never touch the simulation at all.
   *
   * Nothing in the simulation reads these.
   */
  prevX = 0
  prevY = 0
  prevZ = 0

  /** Transient, set each step by the contact solver. Gates rolling resistance. */
  touchingSurface = false

  /**
   * Height of the surface the ball is currently supported by, metres.
   *
   * Zero on the playfield. A ramp sets it to the ramp's own height under the ball, which is
   * how a single flat contact routine serves both without knowing which it is standing on.
   */
  floorZ = 0

  constructor(id: number) {
    this.id = id
  }

  /** Place the ball at rest. Clears velocity, spin and the stillness counter. */
  reset(x: number, y: number, z = 0): void {
    this.x = x
    this.y = y
    this.z = z
    this.prevX = x
    this.prevY = y
    this.prevZ = z
    this.vx = 0
    this.vy = 0
    this.vz = 0
    this.wx = 0
    this.wy = 0
    this.wz = 0
    this.qx = 0
    this.qy = 0
    this.qz = 0
    this.qw = 1
    this.stillSteps = 0
    this.heldBy = -1
    this.touchingSurface = false
    this.floorZ = 0
    this.layer = 0
  }

  /** Planar speed, m/s. */
  speed(): number {
    return Math.sqrt(this.vx * this.vx + this.vy * this.vy)
  }

  /**
   * Set the spin a ball rolling at its current velocity would have: `ω = (ẑ × v) / r`.
   *
   * Whether a ball is rolling or sliding is not a detail — it decides shots. A rolling ball
   * loses `(5/7)` of a slope; a sliding one loses the slope *plus* the full Coulomb drag,
   * which on the standard playfield friction is another 1.95 m/s². A ramp that a rolling
   * ball clears at 1.5 m/s needs 2.0 m/s from a sliding one, a third more.
   *
   * That difference is a real property of the game, not an artefact — it is why a ball
   * flipped hard with backspin can rattle off a ramp the same ball makes easily when it has
   * had a foot of playfield to settle down (PH-A15). It matters here because a test or a
   * device that injects velocity without spin is injecting a *sliding* ball, and will
   * quietly measure the wrong machine.
   */
  setRollingSpin(): void {
    const inv = 1 / this.radius
    this.wx = -this.vy * inv
    this.wy = this.vx * inv
    this.wz = 0
  }

  /**
   * Enforce the hard speed clamp.
   *
   * Not a physical effect — a safety net. A solenoid stack (bumper into sling into
   * flipper on the same step) can otherwise produce a speed that outruns the swept
   * narrow phase, and a ball that escapes the table is unrecoverable.
   *
   * The limit is passed in rather than read from `SPEED_CLAMP` because a machine may set its
   * own (`physics.ballSpeedClamp`, 04 §10). It has to come from the caller and not from a
   * field on the ball: the world owns the value, and one place holding it is one place for the
   * two cores to disagree in rather than seven.
   */
  clampSpeed(limit: number = SPEED_CLAMP): void {
    const s2 = this.vx * this.vx + this.vy * this.vy
    if (s2 > limit * limit) {
      const scale = limit / Math.sqrt(s2)
      this.vx *= scale
      this.vy *= scale
    }
    if (this.vz > limit) this.vz = limit
    else if (this.vz < -limit) this.vz = -limit
  }

  /**
   * Advance the orientation quaternion by the current angular velocity.
   *
   * Rendering only — nothing in the simulation reads the quaternion, so a small
   * integration error here cannot affect play. It is renormalised every step because
   * drift over a long game would otherwise show as the ball visibly shearing.
   */
  integrateOrientation(dt: number): void {
    const hx = this.wx * dt * 0.5
    const hy = this.wy * dt * 0.5
    const hz = this.wz * dt * 0.5

    const nx = this.qw * hx + this.qy * hz - this.qz * hy
    const ny = this.qw * hy + this.qz * hx - this.qx * hz
    const nz = this.qw * hz + this.qx * hy - this.qy * hx
    const nw = -(this.qx * hx + this.qy * hy + this.qz * hz)

    let qx = this.qx + nx
    let qy = this.qy + ny
    let qz = this.qz + nz
    let qw = this.qw + nw

    const len = Math.sqrt(qx * qx + qy * qy + qz * qz + qw * qw)
    if (len > 0) {
      const inv = 1 / len
      qx *= inv
      qy *= inv
      qz *= inv
      qw *= inv
    } else {
      qx = 0
      qy = 0
      qz = 0
      qw = 1
    }

    this.qx = qx
    this.qy = qy
    this.qz = qz
    this.qw = qw
  }
}

/**
 * A ball's whole mutable state (M4-28a).
 *
 * Position, velocity and spin are what a renderer needs. A *simulation* needs six more things, and
 * every one of them was missing until the completeness test compared field by field:
 *
 *   * `heldBy` — the device holding this ball. Without it a restored game has a scoop that is
 *     empty and a ball that is loose inside it, which is the exact case M4-28a names.
 *   * `prevX/Y/Z` — where it was last step, which is the segment the swept guard tests against.
 *     Restored as zero, the first swept test after a resume sweeps from the playfield origin.
 *   * `qx/qy/qz/qw` — orientation. Not physical, but it is state, and a ball that snaps to
 *     upright on a resume is visible.
 *   * `stillSteps` — how long it has been motionless, which is what ball search counts.
 *   * `touchingSurface` and `floorZ` — whether it is on the playfield and how high that is here.
 */
export interface BallSnapshot {
  id: number
  active: boolean
  x: number
  y: number
  z: number
  vx: number
  vy: number
  vz: number
  wx: number
  wy: number
  wz: number
  qx: number
  qy: number
  qz: number
  qw: number
  layer: number
  heldBy: number
  prevX: number
  prevY: number
  prevZ: number
  stillSteps: number
  touchingSurface: boolean
  floorZ: number
}

export function snapshotBall(b: Ball): BallSnapshot {
  return {
    id: b.id,
    active: b.active,
    x: b.x,
    y: b.y,
    z: b.z,
    vx: b.vx,
    vy: b.vy,
    vz: b.vz,
    wx: b.wx,
    wy: b.wy,
    wz: b.wz,
    qx: b.qx,
    qy: b.qy,
    qz: b.qz,
    qw: b.qw,
    layer: b.layer,
    heldBy: b.heldBy,
    prevX: b.prevX,
    prevY: b.prevY,
    prevZ: b.prevZ,
    stillSteps: b.stillSteps,
    touchingSurface: b.touchingSurface,
    floorZ: b.floorZ,
  }
}

export function restoreBall(b: Ball, s: BallSnapshot): void {
  b.active = s.active
  b.x = s.x
  b.y = s.y
  b.z = s.z
  b.vx = s.vx
  b.vy = s.vy
  b.vz = s.vz
  b.wx = s.wx
  b.wy = s.wy
  b.wz = s.wz
  b.qx = s.qx
  b.qy = s.qy
  b.qz = s.qz
  b.qw = s.qw
  b.layer = s.layer
  b.heldBy = s.heldBy
  b.prevX = s.prevX
  b.prevY = s.prevY
  b.prevZ = s.prevZ
  b.stillSteps = s.stillSteps
  b.touchingSurface = s.touchingSurface
  b.floorZ = s.floorZ
}

export { BALL_RADIUS }
